import { Injectable, signal, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { DataService } from './data.service';
import { User } from '../models/firestore.types';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private dataService = inject(DataService);
  private auth = inject(Auth);

  currentUser = signal<User | null>(null);

  async getUser(userId?: string): Promise<User | null> {
    const uid = userId || this.auth.currentUser?.uid;
    if (!uid) return null;

    const cached = this.currentUser();
    if (!userId && cached?.id === uid) {
      return cached;
    }

    const user = await this.dataService.get<User>('users', uid);
    if (user && !userId) {
      this.currentUser.set({ ...user, id: user.id || uid });
    }
    return user;
  }

  async createUser(userId: string, userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
    await this.dataService.merge('users', userId, userData);
    this.currentUser.set({ ...userData as User, id: userId });
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    await this.dataService.update('users', userId, updates);

    if (userId === this.auth.currentUser?.uid) {
      const current = this.currentUser();
      if (current) {
        this.currentUser.set({ ...current, ...updates });
      }
    }
  }

  async updateCurrentContext(context: { type: 'team' | 'league' | 'organization'; id: string }): Promise<void> {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;

    await this.updateUser(uid, { currentContext: context });
  }

  clearUser(): void {
    this.currentUser.set(null);
  }
}